import React, { useState, useEffect } from 'react';
import { Wrench, Mail, Phone, RefreshCw, ChevronDown, ChevronUp, MapPin, Building2, AlertCircle, Send, DollarSign, CheckCircle2, TrendingDown, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS = ['pending', 'reviewing', 'quoted', 'approved', 'declined', 'in_progress', 'completed', 'cancelled'];
const statusColors = {
  pending: 'bg-amber-500',
  reviewing: 'bg-blue-500',
  quoted: 'bg-cyan-500',
  approved: 'bg-indigo-500',
  declined: 'bg-rose-500',
  in_progress: 'bg-purple-500',
  completed: 'bg-green-600',
  cancelled: 'bg-gray-500',
};

const RepairQuotesTab = ({ onPendingCount }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [rushOnly, setRushOnly] = useState(false);
  const [quoteForms, setQuoteForms] = useState({}); // { [id]: { price, message, sending } }
  const [trend, setTrend] = useState([]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/repair-quotes`, { headers });
      setQuotes(res.data || []);
    } catch (err) {
      toast.error('Failed to load diagnostic quotes');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrend = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/repair-quotes/win-rate-trend?months=6`, { headers });
      setTrend(res.data || []);
    } catch (err) { /* non-critical */ }
  };

  useEffect(() => { fetchQuotes(); fetchTrend(); /* eslint-disable-next-line */ }, []);

  // Report the pending count up to the sidebar whenever the list changes.
  useEffect(() => {
    if (onPendingCount) onPendingCount(quotes.filter((q) => q.status === 'pending').length);
  }, [quotes, onPendingCount]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/repair-quotes/${id}/status?status=${status}`, {}, { headers });
      setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success(`Status updated to ${status.replace('_', ' ')}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const setQF = (id, patch) => setQuoteForms((p) => ({ ...p, [id]: { ...(p[id] || {}), ...patch } }));

  const sendQuote = async (id) => {
    const form = quoteForms[id] || {};
    if (!form.price || !form.price.trim()) { toast.error('Enter a price to send.'); return; }
    setQF(id, { sending: true });
    try {
      await axios.post(`${API_URL}/api/admin/repair-quotes/${id}/send-quote`, { price: form.price, message: form.message || '' }, { headers });
      setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status: 'quoted', quote_amount: form.price, quote_sent_at: new Date().toISOString() } : q)));
      toast.success('Quote emailed to the customer');
      setQF(id, { sending: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send quote');
      setQF(id, { sending: false });
    }
  };

  const displayed = rushOnly ? quotes.filter((q) => q.rush) : quotes;
  const rushCount = quotes.filter((q) => q.rush).length;

  // Decline insights: breakdown of declined quotes by reason.
  const declined = quotes.filter((q) => q.status === 'declined');
  const declineTotal = declined.length;
  const reasonCounts = declined.reduce((acc, q) => {
    const r = (q.decline_reason || '').trim() || 'No reason given';
    acc[r] = (acc[r] || 0) + 1;
    return acc;
  }, {});
  const reasonRows = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1]);

  // Win-rate snapshot.
  const approvedCount = quotes.filter((q) => q.status === 'approved').length;
  const pendingCount = quotes.filter((q) => q.status === 'pending').length;
  const decided = approvedCount + declineTotal;
  const winRate = decided > 0 ? Math.round((approvedCount / decided) * 100) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="repair-quotes-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#003456]">Diagnostic Quotes</h2>
          <p className="text-gray-500">Requests submitted through the free diagnostic quote form</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setRushOnly((v) => !v)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors border ${rushOnly ? 'bg-red-600 border-red-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-red-300'}`}
            data-testid="rq-rush-filter"
          >
            <AlertCircle className="h-4 w-4" /> Rush only {rushCount > 0 && <span className="ml-1 bg-white/25 px-1.5 rounded-full">{rushCount}</span>}
          </button>
          <button onClick={() => { fetchQuotes(); fetchTrend(); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-[#003456] text-white hover:bg-[#023F63]" data-testid="rq-refresh">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {quotes.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="rq-winrate">
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-gray-500">Approved</p>
            <p className="text-2xl font-bold text-indigo-600" data-testid="rq-stat-approved">{approvedCount}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-gray-500">Declined</p>
            <p className="text-2xl font-bold text-rose-500" data-testid="rq-stat-declined">{declineTotal}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-amber-500" data-testid="rq-stat-pending">{pendingCount}</p>
          </div>
          <div className="bg-[#003456] rounded-xl p-4">
            <p className="text-xs text-white/60">Win rate</p>
            <p className="text-2xl font-bold text-[#CE8946]" data-testid="rq-stat-winrate">{winRate === null ? '—' : `${winRate}%`}</p>
            <p className="text-[10px] text-white/40 mt-0.5">of decided quotes</p>
          </div>
        </div>
      )}

      {trend.some((t) => t.decided > 0) && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-6" data-testid="rq-winrate-trend">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-[#CE8946]" />
            <h3 className="text-sm font-bold text-[#003456]">Win rate trend</h3>
            <span className="text-xs text-gray-400">last 6 months</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trend} margin={{ top: 5, right: 12, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: '#64748b' }} unit="%" />
              <Tooltip
                formatter={(v, n, p) => [`${v}% (${p.payload.approved}/${p.payload.decided} approved)`, 'Win rate']}
                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
              />
              <Line type="monotone" dataKey="win_rate" stroke="#CE8946" strokeWidth={3} dot={{ r: 4, fill: '#003456' }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-[11px] text-gray-400 mt-1">Approved out of decided (approved + declined) quotes each month.</p>
        </div>
      )}

      {declineTotal > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-6" data-testid="rq-decline-insights">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown className="h-4 w-4 text-rose-500" />
            <h3 className="text-sm font-bold text-[#003456]">Why quotes fall through</h3>
            <span className="text-xs text-gray-400">({declineTotal} declined)</span>
          </div>
          <div className="space-y-2">
            {reasonRows.map(([reason, count]) => {
              const pct = Math.round((count / declineTotal) * 100);
              return (
                <div key={reason} className="flex items-center gap-3" data-testid={`rq-insight-${reason.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}>
                  <span className="w-48 text-sm text-gray-600 flex-shrink-0 truncate">{reason}</span>
                  <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-rose-400 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-sm font-bold text-[#003456] w-14 text-right flex-shrink-0">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {displayed.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-500 border border-slate-100" data-testid="rq-empty">
          <Wrench className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          {rushOnly ? 'No rush requests right now.' : 'No diagnostic quote requests yet.'}
        </div>
      ) : (
        <div className="space-y-3">
          {displayed.map((q) => (
            <div key={q.id} className={`bg-white rounded-xl border overflow-hidden ${q.rush ? 'border-red-300' : 'border-slate-100'}`} data-testid={`rq-row-${q.id}`}>
              <button
                onClick={() => setExpanded(expanded === q.id ? null : q.id)}
                className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#003456]">{q.name || 'No name'}</span>
                    {q.rush && (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-white bg-red-600 px-2 py-0.5 rounded-full animate-pulse" data-testid="rq-rush-badge">
                        🔥 RUSH
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mt-1 flex-wrap">
                    <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {q.email}</span>
                    {q.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {q.phone}</span>}
                    <span className="text-gray-400">{q.brand} {q.model} · {q.issue_type}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {q.quote_sent_at && (
                    <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-[#B87333] bg-[#CE8946]/15 px-2 py-1 rounded" data-testid={`rq-quote-history-${q.id}`} title={`Quoted on ${new Date(q.quote_sent_at).toLocaleString()}`}>
                      <DollarSign className="h-3 w-3" />
                      {q.quote_amount || 'Quoted'} · {new Date(q.quote_sent_at).toLocaleDateString()}
                    </span>
                  )}
                  <span className={`text-xs font-medium px-2 py-1 rounded text-white ${statusColors[q.status] || 'bg-gray-400'}`}>
                    {(q.status || 'pending').replace('_', ' ')}
                  </span>
                  {expanded === q.id ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </div>
              </button>

              {expanded === q.id && (
                <div className="px-4 pb-5 pt-1 border-t border-slate-100 bg-slate-50/50" data-testid={`rq-detail-${q.id}`}>
                  {q.rush && (
                    <div className="mt-4 mb-4 border-2 border-red-500 bg-red-50 rounded-lg p-3 flex items-center gap-3" data-testid="rq-rush-detail">
                      <span className="text-xl">🔥</span>
                      <p className="text-sm font-semibold text-red-700">Rush turnaround requested — prioritise a callback.</p>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm mt-4">
                    {q.company && <p><span className="text-gray-500">Company:</span> <span className="text-[#003456] font-medium inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{q.company}</span></p>}
                    <p><span className="text-gray-500">Location:</span> <span className="text-[#003456] font-medium inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{[q.city, q.province, q.country].filter(Boolean).join(', ') || '—'}</span></p>
                    <p><span className="text-gray-500">Machine size:</span> <span className="text-[#003456] font-medium">{q.size || '—'}</span></p>
                    <p><span className="text-gray-500">Serial:</span> <span className="text-[#003456] font-medium">{q.serial_number || '—'}</span></p>
                    <p><span className="text-gray-500">Services:</span> <span className="text-[#003456] font-medium">{(q.services || []).join(', ') || '—'}</span></p>
                    <p><span className="text-gray-500">Urgency:</span> <span className="text-[#003456] font-medium">{q.urgency || '—'}</span></p>
                    {q.error_codes && <p><span className="text-gray-500">Error codes:</span> <span className="text-[#003456] font-medium">{q.error_codes}</span></p>}
                    {q.promo_code && <p><span className="text-gray-500">Offer code:</span> <span className="text-[#003456] font-medium">{q.promo_code}</span></p>}
                    {q.decline_reason && <p><span className="text-gray-500">Decline reason:</span> <span className="text-rose-600 font-semibold" data-testid={`rq-decline-reason-${q.id}`}>{q.decline_reason}</span></p>}
                  </div>
                  {q.issue_description && (
                    <div className="mt-4">
                      <p className="text-gray-500 text-sm mb-1">Issue description</p>
                      <p className="text-sm text-[#003456] bg-white border border-slate-200 rounded-lg p-3 whitespace-pre-wrap">{q.issue_description}</p>
                    </div>
                  )}
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-sm text-gray-500">Update status:</span>
                    <select
                      value={q.status || 'pending'}
                      onChange={(e) => updateStatus(q.id, e.target.value)}
                      className="text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#CE8946]"
                      data-testid={`rq-status-${q.id}`}
                    >
                      {STATUS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                    </select>
                  </div>

                  {/* Send quote */}
                  <div className="mt-5 border-t border-slate-200 pt-4" data-testid={`rq-sendquote-${q.id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-4 w-4 text-[#CE8946]" />
                      <h4 className="font-bold text-[#003456] text-sm">Send quote to customer</h4>
                      {q.quote_sent_at && (
                        <span className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                          <CheckCircle2 className="h-3 w-3" /> Sent {q.quote_amount ? `· ${q.quote_amount}` : ''}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Emails {q.email} a branded quote and marks this request as "quoted".</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        value={(quoteForms[q.id] || {}).price || ''}
                        onChange={(e) => setQF(q.id, { price: e.target.value })}
                        placeholder="Price (e.g. $850 CAD)"
                        className="sm:w-48 text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#CE8946]"
                        data-testid={`rq-quote-price-${q.id}`}
                      />
                      <input
                        type="text"
                        value={(quoteForms[q.id] || {}).message || ''}
                        onChange={(e) => setQF(q.id, { message: e.target.value })}
                        placeholder="Optional note to the customer"
                        className="flex-1 text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#CE8946]"
                        data-testid={`rq-quote-message-${q.id}`}
                      />
                      <button
                        onClick={() => sendQuote(q.id)}
                        disabled={(quoteForms[q.id] || {}).sending}
                        className="inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold text-sm px-4 py-2 rounded-md transition-colors disabled:opacity-60"
                        data-testid={`rq-send-quote-${q.id}`}
                      >
                        {(quoteForms[q.id] || {}).sending ? 'Sending…' : (<><Send className="h-4 w-4" /> Send quote</>)}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RepairQuotesTab;
